import { useState, useEffect } from 'react';
import { Search, Bell, Bot, CheckCircle2, XCircle, RefreshCw, Play } from 'lucide-react';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AgentConfig } from '../../types/agent';
import { Badge, EmptyState } from '../shared';

export function AgentConfiguration() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [agentName, setAgentName] = useState('');
  const [agentDescription, setAgentDescription] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [personalityTraits, setPersonalityTraits] = useState<string[]>([]);
  const [newTrait, setNewTrait] = useState('');

  useEffect(() => {
    loadConfig();
  }, [user]);

  useEffect(() => {
    if (agentConfig) {
      setAgentName(agentConfig.agentName);
      setAgentDescription(agentConfig.agentDescription);
      setBrandVoice(agentConfig.brandVoice);
      setPersonalityTraits(agentConfig.personalityTraits);
    }
  }, [agentConfig]);

  const loadConfig = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'agentConfigs'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setAgentConfig(snapshot.docs[0].data() as AgentConfig);
      }
    } catch (error) {
      console.error('Error loading agent config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const configData: Partial<AgentConfig> = {
        userId: user.uid,
        agentName,
        agentDescription,
        brandVoice,
        personalityTraits,
        updatedAt: Timestamp.now()
      };

      if (agentConfig) {
        await updateDoc(doc(db, 'agentConfigs', agentConfig.id), configData);
      } else {
        const newConfig: Omit<AgentConfig, 'id'> = {
          ...configData as any,
          mcpEndpoint: `https://mcp.kodiak.ai/agent/${user.uid}`,
          restApiEndpoint: `https://api.kodiak.ai/agent/${user.uid}`,
          apiKey: generateApiKey(),
          deploymentStatus: {
            mcp: 'inactive',
            rest: 'inactive',
            knowledgeSync: 'never_synced'
          },
          createdAt: Timestamp.now()
        };
        await setDoc(doc(collection(db, 'agentConfigs')), newConfig);
      }

      await loadConfig();
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const generateApiKey = () => {
    return 'ka_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const addPersonalityTrait = () => {
    if (newTrait.trim() && !personalityTraits.includes(newTrait.trim())) {
      setPersonalityTraits([...personalityTraits, newTrait.trim()]);
      setNewTrait('');
    }
  };

  const removeTrait = (trait: string) => {
    setPersonalityTraits(personalityTraits.filter(t => t !== trait));
  };

  const handleTestAgent = () => {
    alert('Test agent functionality coming soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Configuration</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
            <h1 className="text-3xl font-semibold text-gray-900">Brand Agent Configuration</h1>
            <p className="text-sm text-gray-600 mt-1">Define your agent's identity, personality, and deployment settings</p>
          </div>

          {agentConfig && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700"
            >
              Edit Configuration
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Agent Identity Panel */}
          <div className="col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-6">
              <Bot className="w-5 h-5 text-t3-primary" />
              <h2 className="text-base font-semibold text-gray-900">Agent Identity</h2>
            </div>

            {!agentConfig && !isEditing ? (
              <EmptyState
                icon={Bot}
                title="No agent configured"
                description="Create your brand agent to start engaging with AI platforms"
              />
            ) : (
              <div className="space-y-6">
                {/* Agent Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      placeholder="e.g., Acme AI Assistant"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t3-primary"
                    />
                  ) : (
                    <p className="text-base text-gray-900">{agentConfig?.agentName || 'Not set'}</p>
                  )}
                </div>

                {/* Agent Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  {isEditing ? (
                    <textarea
                      value={agentDescription}
                      onChange={(e) => setAgentDescription(e.target.value)}
                      placeholder="Describe what your agent does and who it helps..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t3-primary"
                    />
                  ) : (
                    <p className="text-base text-gray-900">{agentConfig?.agentDescription || 'Not set'}</p>
                  )}
                </div>

                {/* Brand Voice */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand Voice</label>
                  {isEditing ? (
                    <textarea
                      value={brandVoice}
                      onChange={(e) => setBrandVoice(e.target.value)}
                      placeholder="Describe your brand's tone and communication style..."
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t3-primary"
                    />
                  ) : (
                    <p className="text-base text-gray-900">{agentConfig?.brandVoice || 'Not set'}</p>
                  )}
                </div>

                {/* Personality Traits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Personality Traits</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {personalityTraits.map((trait, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-t3-primary bg-opacity-10 text-t3-primary rounded-full text-sm flex items-center gap-2"
                      >
                        {trait}
                        {isEditing && (
                          <button
                            onClick={() => removeTrait(trait)}
                            className="text-t3-primary hover:text-purple-700"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {isEditing && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTrait}
                        onChange={(e) => setNewTrait(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addPersonalityTrait()}
                        placeholder="Add a trait (e.g., helpful, professional, friendly)"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-t3-primary"
                      />
                      <button
                        onClick={addPersonalityTrait}
                        className="px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700"
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {isEditing && (
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        if (agentConfig) {
                          setAgentName(agentConfig.agentName);
                          setAgentDescription(agentConfig.agentDescription);
                          setBrandVoice(agentConfig.brandVoice);
                          setPersonalityTraits(agentConfig.personalityTraits);
                        }
                      }}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {!agentConfig && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="w-full mt-4 px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700"
              >
                Create Agent
              </button>
            )}
          </div>

          {/* Deployment Status Panel */}
          <div className="space-y-6">
            {/* Deployment Status */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Deployment Status</h2>

              {!agentConfig ? (
                <p className="text-sm text-gray-600">Configure your agent to enable deployment</p>
              ) : (
                <div className="space-y-4">
                  {/* MCP Status */}
                  <div className="flex items-center justify-between p-3 bg-surface-warm rounded-lg">
                    <div className="flex items-center gap-2">
                      {agentConfig.deploymentStatus.mcp === 'active' ? (
                        <CheckCircle2 className="w-5 h-5 text-success-green" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="text-sm font-medium text-gray-900">MCP Endpoint</span>
                    </div>
                    <Badge variant={agentConfig.deploymentStatus.mcp === 'active' ? 'success' : 'critical'}>
                      {agentConfig.deploymentStatus.mcp}
                    </Badge>
                  </div>

                  {/* REST API Status */}
                  <div className="flex items-center justify-between p-3 bg-surface-warm rounded-lg">
                    <div className="flex items-center gap-2">
                      {agentConfig.deploymentStatus.rest === 'active' ? (
                        <CheckCircle2 className="w-5 h-5 text-success-green" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="text-sm font-medium text-gray-900">REST API</span>
                    </div>
                    <Badge variant={agentConfig.deploymentStatus.rest === 'active' ? 'success' : 'critical'}>
                      {agentConfig.deploymentStatus.rest}
                    </Badge>
                  </div>

                  {/* Knowledge Sync Status */}
                  <div className="flex items-center justify-between p-3 bg-surface-warm rounded-lg">
                    <div className="flex items-center gap-2">
                      {agentConfig.deploymentStatus.knowledgeSync === 'synced' ? (
                        <CheckCircle2 className="w-5 h-5 text-success-green" />
                      ) : agentConfig.deploymentStatus.knowledgeSync === 'syncing' ? (
                        <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="text-sm font-medium text-gray-900">Knowledge Sync</span>
                    </div>
                    <Badge
                      variant={
                        agentConfig.deploymentStatus.knowledgeSync === 'synced'
                          ? 'success'
                          : agentConfig.deploymentStatus.knowledgeSync === 'syncing'
                          ? 'primary'
                          : 'critical'
                      }
                    >
                      {agentConfig.deploymentStatus.knowledgeSync}
                    </Badge>
                  </div>

                  {agentConfig.deploymentStatus.lastSynced && (
                    <p className="text-xs text-gray-600 text-center">
                      Last synced: {agentConfig.deploymentStatus.lastSynced.toDate().toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Test Agent */}
            {agentConfig && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Test Agent</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Test your agent configuration with sample queries before deployment
                </p>
                <button
                  onClick={handleTestAgent}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-t3-primary text-white rounded-lg hover:bg-purple-700"
                >
                  <Play className="w-4 h-4" />
                  Test Agent
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
